import _ from 'lodash';
import { getIntl } from 'umi';

import { CoreEnums } from '@/utils/domain/constants';
import {
  getBulkImportErrorData,
  getFiltersGlobalFilters,
  toPaginate,
} from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

const { BulkActionType } = CoreEnums;
function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function createCrudModel(config) {
  const {
    namespace,
    fetchRoute,
    fetchParams,
    entityName,
    api,
    state: extraState,
    effects: extraEffects,
    reducers: extraReducers,
    subscriptions: extraSubscriptions,
    features = {},
  } = config;

  const withCrud = features.crud !== false;
  const withImport = withCrud && features.import !== false;
  const withBulkActions = withCrud && features.bulkActions !== false;
  const withListCache = withCrud && features.listCache === true;

  // repeatableLatestFetch: name of the fetch effect to re-dispatch (e.g. 'fetchData').
  // Generates: saveLatestFetch reducer, repeatLatestFetch effect, latestFetchPayload state key.
  const repeatableFetchEffect = features.repeatableLatestFetch || null;
  const initialState = {
    orderBy: '',
    ...(withCrud && {
      list: [],
      pagination: { page: 1, count: 0, totalPages: 0 },
      select: CoreQueries[fetchParams].paged.select,
      searchSelect: CoreQueries[fetchParams].paged.searchSelect,
      showForm: false,
      detail: false,
      id: null,
      item: null,
      activatingItemIds: {},
    }),
    success: false,
    validationErrors: [],
    ...(repeatableFetchEffect && { latestFetchPayload: null }),
    ...(withListCache && { listCache: null }),
    ...(withImport && {
      showImport: false,
      bulkActionErrorData: null,
      showBulkActionErrors: false,
    }),
    ...(extraState || {}),
  };

  const generatedEffects = {
    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },
  };

  if (repeatableFetchEffect) {
    generatedEffects.saveLatestFetch = function* ({ payload }, { put }) {
      yield put({ type: 'queryLatestFetchPayload', payload });
    };

    generatedEffects.repeatLatestFetch = function* (__, { put, select }) {
      const latestPayload = yield select((state) => state[namespace].latestFetchPayload);
      yield put({ type: repeatableFetchEffect, payload: latestPayload });
    };
  }

  if (withCrud) {
    generatedEffects.fetchList = function* ({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const limit = payload.limit ?? pageSize;
        const page = payload.page ?? 1;

        if (withListCache) {
          const cacheKey = `${namespace}-${page}-${limit}-${payload?.search || ''}-${
            payload?.filter || ''
          }`;
          const cachedState = yield select((s) => s[namespace]);
          if (cachedState.listCache && cachedState.listCache.cacheKey === cacheKey) {
            yield put({
              type: 'queryData',
              payload: { ...cachedState.listCache.response, cacheKey, limit, page },
            });
            return;
          }
          const params = { ...CoreQueries[fetchParams].paged, pageSize, ...payload };
          const res = yield call(api.getAll, {
            route: `${fetchRoute}/paged`,
            params,
            readPriority: true,
          });
          yield put({ type: 'queryData', payload: { ...res, limit, page, cacheKey } });
          return;
        }

        const params = { ...CoreQueries[fetchParams].paged, pageSize, ...payload };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({ type: 'queryData', payload: { ...res, limit, page } });
      } catch (e) {}
    };

    generatedEffects.fetchItem = function* ({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {}
    };

    generatedEffects.fetchCreate = function* ({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: fetchRoute, data: payload.data });
        if (withListCache) yield put({ type: 'clearListCache' });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            // Pass through so the generic banner is suppressed and
            // useConfigForm can paint per-field messages.
            validationErrors: e.validationErrors || [],
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    };

    generatedEffects.fetchUpdate = function* ({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, { route: fetchRoute, data: payload.data });
        if (withListCache) yield put({ type: 'clearListCache' });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    };

    generatedEffects.fetchDelete = function* ({ payload }, { call, put }) {
      try {
        const { id, view } = payload;

        yield call(api.remove, { route: fetchRoute, id });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        if (withListCache) yield put({ type: 'clearListCache' });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
      }
    };

    generatedEffects.fetchActivate = function* ({ payload }, { put, call }) {
      const { id } = payload;
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        yield call(api.activate, { route: fetchRoute, id, isActive: payload.state });
        if (withListCache) yield put({ type: 'clearListCache' });
        yield put({ type: `${entityName}Activate`, payload });
      } catch (e) {
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    };

    generatedEffects.showForm = function* ({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) yield put({ type: 'editOrDetail', payload });
    };

    generatedEffects.setPaginationPage = function* ({ payload }, { put }) {
      yield put({ type: 'queryPaginationPage', payload });
    };
  }

  if (withBulkActions) {
    generatedEffects[`bulkDelete${capitalize(namespace)}`] = function* (
      { payload },
      { call, put, select },
    ) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateModel = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);

        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateModel.orderBy && { orderBy: stateModel.orderBy }),
          ...(filters && { filter: filters }),
          select: stateModel.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    };

    generatedEffects[`bulkActivate${capitalize(namespace)}`] = function* (
      { payload },
      { call, put },
    ) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });

        yield put({
          type: `bulk${capitalize(entityName)}Activate`,
          payload: { ids, isActive: true },
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    };

    generatedEffects[`bulkDeactivate${capitalize(namespace)}`] = function* (
      { payload },
      { call, put },
    ) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });

        yield put({
          type: `bulk${capitalize(entityName)}Activate`,
          payload: { ids, isActive: false },
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    };
  }

  if (withImport) {
    generatedEffects.toggleImportComponent = function* ({ payload }, { put }) {
      yield put({ type: 'queryToggleImportComponent', payload: { showImport: payload.show } });
    };

    generatedEffects.fetchImport = function* ({ payload }, { call, put, select }) {
      try {
        const { file, isDeleteData } = payload;

        const pageSize = yield select((state) => state.global.pageSize);
        const stateModel = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);

        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateModel.orderBy && { orderBy: stateModel.orderBy }),
          ...(filters && { filter: filters }),
        };

        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import`,
          method: 'post',
          data: { fileContent: file, deleteExistingData: isDeleteData },
        });

        const errorData = getBulkImportErrorData(res, getIntl);
        const hasImportedRows = res.importedRowsCount && res.importedRowsCount > 0;

        if (errorData) {
          yield put({ type: 'setBulkActionErrors', payload: { errorData } });
          yield put({ type: 'toggleBulkActionErrorsDrawer', payload: { open: true } });
        }

        yield put({ type: 'queryImport', payload: { ...res } });
        if (hasImportedRows) yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    };

    generatedEffects.toggleBulkActionErrorsDrawer = function* ({ payload }, { put }) {
      yield put({ type: 'queryToggleBulkActionErrorsDrawer', payload });
    };

    generatedEffects.clearBulkActionErrors = function* (_, { put }) {
      yield put({ type: 'queryClearBulkActionErrors' });
    };
  }

  const generatedReducers = {
    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    reset() {
      return { ...initialState };
    },

    queryOrderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },
  };

  if (withCrud) {
    generatedReducers.queryData = function (state, { payload }) {
      const { cacheKey, ...rest } = payload;
      const list = (rest.items || []).map((item) => ({
        ...item,
        isActive: item.isActive ?? false,
      }));
      const nextState = {
        ...state,
        list,
        pagination: _.merge(state.pagination, toPaginate(rest)),
      };
      if (cacheKey) {
        nextState.listCache = { cacheKey, response: { ...rest, items: list } };
      }
      return nextState;
    };

    generatedReducers.queryActivatingItem = function (state, { payload }) {
      const { id, ids, active } = payload || {};
      const activatingItemIds = { ...(state.activatingItemIds || {}) };

      if (Array.isArray(ids) && ids.length) {
        ids.forEach((itemId) => {
          if (itemId == null) return;
          if (active) {
            activatingItemIds[itemId] = true;
          } else {
            delete activatingItemIds[itemId];
          }
        });
        return { ...state, activatingItemIds };
      }

      if (id == null) {
        return { ...state, activatingItemIds: {} };
      }

      if (active) {
        activatingItemIds[id] = true;
      } else {
        delete activatingItemIds[id];
      }
      return { ...state, activatingItemIds };
    };

    generatedReducers[`${entityName}Activate`] = function (state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    };

    generatedReducers[`bulk${capitalize(entityName)}Activate`] = function (state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    };

    generatedReducers.queryItem = function (state, { payload }) {
      return { ...state, item: payload };
    };

    generatedReducers.queryShowForm = function (state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    };

    generatedReducers.editOrDetail = function (state, { payload }) {
      return { ...state, item: payload.item ?? {} };
    };

    generatedReducers.queryPaginationPage = function (state, { payload }) {
      return { ...state, pagination: { ...state.pagination, page: payload } };
    };

    if (withListCache) {
      generatedReducers.clearListCache = function (state) {
        return { ...state, listCache: null };
      };
    }
  }

  if (withImport) {
    generatedReducers.queryToggleImportComponent = function (state, { payload }) {
      return { ...state, showImport: payload.showImport ?? payload.show };
    };

    generatedReducers.queryImport = function (state) {
      return { ...state, showImport: false };
    };

    generatedReducers.setBulkActionErrors = function (state, { payload }) {
      return { ...state, bulkActionErrorData: payload.errorData, showBulkActionErrors: true };
    };

    generatedReducers.queryToggleBulkActionErrorsDrawer = function (state, { payload }) {
      return { ...state, showBulkActionErrors: payload.open };
    };

    generatedReducers.queryClearBulkActionErrors = function (state) {
      return { ...state, bulkActionErrorData: null, showBulkActionErrors: false };
    };
  }

  return {
    namespace,
    state: initialState,
    reducers: { ...generatedReducers, ...(extraReducers || {}) },
    effects: { ...generatedEffects, ...(extraEffects || {}) },
    subscriptions: extraSubscriptions || {},
  };
}
